<template>
  <div
    class="references-list"
    :class="{ readonly: !isEditable }"
    @drag="handleDrag"
    @drop="handleDrop"
    @dragover.prevent
    @click="handleOutsideClick"
  >
    <LineButton
      v-if="isEditable && selectedReference?.index !== 0"
      :tooltip="t('author.addNewReference')"
      @click-btn.stop="addReference(0)"
    />
    <TextBody
      v-if="!referencesList.length && !isEditable"
      tag="span"
      i18n-key="author.noReferences"
      :body-type="3"
      weight="medium"
    />
    <ReferenceListItem
      v-else
      v-for="(reference, index) in referencesList"
      :key="`reference-${index}`"
      :index="index"
      :reference="reference"
      :is-selected="isReferenceSelected(reference)"
      :is-editable="isEditable"
      :is-dragging="draggedIndex === reference.index"
      @select="onSelectReference(reference)"
      @move-up="() => moveUp(index)"
      @move-down="() => moveDown(index)"
      @remove="() => removeFootnote(index)"
      @update="(value) => onUpdateFootnote(reference, value)"
      @add-new-reference="addReference"
    />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useWesley } from '@anthill-modules/wesley-components';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';

import { GetUserData } from '~/constants/getters';

import ReferenceListItem from '~/components/common/references/ReferenceListItem.vue';
import LineButton from '~/components/common/LineButton.vue';
import { footnotesWithAuthorsTitleYearPage } from '~/utils/footnotes';
import TextBody from '~/components/common/TextBody.vue';
import useAuthoring from '~/composables/useAuthoring';
import useModals from '~/composables/useModals';
import useDocumentEditor from '~/composables/useDocumentEditor';
import store from '~/store';

export default {
  components: {
    TextBody,
    ReferenceListItem,
    LineButton,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-references'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const draggedIndex = ref(-1);
    const { sendMessage: sendMessageToDocument } = useWesley();
    const { footnotes, setFootnotes } = useAuthoring();
    const { setReferencesListOpened } = useModals();
    const { updateEditorSelectableElementsData } = useDocumentEditor();
    const userData = computed(() => store.getters[`authStore/${GetUserData}`]);

    const referencesList = computed(() =>
      footnotes.value?.map((ref, index) => ({
        ...ref,
        footnoteString: footnotesWithAuthorsTitleYearPage(ref, false),
        index,
      })),
    );
    const selectedReference = ref(null);

    const onSelectReference = (reference) => {
      if (reference && props.isEditable) {
        selectedReference.value = reference;
      } else {
        const citationData = {
          id: reference.externalId || reference.id,
          page: 1,
          index: 1,
        };

        sendMessageToDocument({
          method: 'editorCommand',
          params: {
            command: {
              insertCitation: citationData,
            },
          },
        });
        updateEditorSelectableElementsData();
        setReferencesListOpened({ open: false });
        emit('update-references');
      }
    };

    const isReferenceSelected = (reference) =>
      selectedReference.value &&
      selectedReference.value.externalId === reference.externalId &&
      selectedReference.value.index === reference.index;

    const onUpdateFootnote = (footnote, text) => {
      if (footnote && text && props.isEditable) {
        const updatedList = referencesList.value.map((item) => {
          if (item.index === footnote.index) {
            if (item.addedByUser) {
              return { ...item, name: text, customFootnote: text };
            }
            return { ...item, customFootnote: text };
          }
          return item;
        });
        setFootnotes(updatedList);
      }
    };

    const handleDrag = (event) => {
      draggedIndex.value = parseInt(event.target.dataset.index, 10);
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', draggedIndex.value); // Pass the index of the dragged item
      event.target.classList.add('dragging-item'); // Add dragging styles
    };

    const handleDrop = (event) => {
      const targetElement = event.target.closest('.draggable-wrapper');
      const targetIndex = targetElement ? parseInt(targetElement.dataset.index, 10) : NaN;

      if (!isNaN(draggedIndex.value) && !isNaN(targetIndex) && draggedIndex.value !== targetIndex) {
        const updatedList = [...referencesList.value];
        const draggedItem = updatedList.splice(draggedIndex.value, 1)[0];
        updatedList.splice(targetIndex, 0, draggedItem);
        updateIndexAndFoootnotesList(updatedList);
      }

      draggedIndex.value = -1; // Reset draggedIndex after the drop
    };

    const updateIndexAndFoootnotesList = (updatedList) => {
      updatedList.forEach((item, newIndex) => {
        item.index = newIndex;
      });

      const updatedFootnotes = updatedList.sort((a, b) => a.index - b.index); // Sort by index

      setFootnotes(updatedFootnotes);
      selectedReference.value = null;
    };

    const removeFootnote = (indexToRemove) => {
      const originalList = [...referencesList.value];
      const updatedList = originalList.filter((_, index) => index !== indexToRemove);

      updateIndexAndFoootnotesList(updatedList);
    };

    const moveUp = (index) => {
      if (index > 0) {
        const updatedList = [...referencesList.value];
        [updatedList[index - 1], updatedList[index]] = [updatedList[index], updatedList[index - 1]];

        updateIndexAndFoootnotesList(updatedList);
      }
    };

    const moveDown = (index) => {
      if (index < referencesList.value.length - 1) {
        const updatedList = [...referencesList.value];
        [updatedList[index + 1], updatedList[index]] = [updatedList[index], updatedList[index + 1]];

        updateIndexAndFoootnotesList(updatedList);
      }
    };

    const handleOutsideClick = (event) => {
      const reference = event.target.closest('.references-wrapper');

      if (!reference) {
        selectedReference.value = null;
      }
    };

    const getDefaultReference = (index) => ({
      id: uuidv4(),
      addedByUser: userData.value,
      name: t('author.typeReferencePlaceholder'),
      customFootnote: t('author.typeReferencePlaceholder'),
      pageNumber: [1],
      index,
    });

    const addReference = (referenceIndex) => {
      const updatedList = [...referencesList.value];
      const index = referenceIndex >= 0 && referenceIndex < updatedList.length ? referenceIndex : updatedList.length;

      const reference = getDefaultReference(index);
      // insert the asset at position index
      updatedList.splice(index, 0, reference);
      updateIndexAndFoootnotesList(updatedList);
    };

    return {
      referencesList,
      isReferenceSelected,
      handleDrag,
      handleDrop,
      selectedReference,
      onSelectReference,
      moveUp,
      moveDown,
      removeFootnote,
      onUpdateFootnote,
      addReference,
      draggedIndex,
      handleOutsideClick,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.references-list {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
  padding: 16px;
  overflow-y: auto;
  max-height: 500px;

  &.readonly {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .dragging-item {
    opacity: 0.5;
    background-color: #f0f0f0;
  }
}
</style>
